/*
Authentication context. Exposes the current user state (id + role) to
the rest of the SPA, plus the two-step passwordless flow:
  - requestCode(email)            → triggers the email send
  - verifyCode(email, code)       → consumes the code, opens the session
plus logout().
*/

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";

import { api, getToken, setToken, setUnauthorizedHandler } from "./api";

// sessionStorage key read by LoginPage to render the "session
// expired" banner on the next render. Stored under sessionStorage
// (not localStorage) because the flag is meaningful only for the
// current tab — a fresh tab opens straight to /login without prior
// context.
const kSessionExpiredFlag = "nfcreader.sessionExpired";

export function consumeSessionExpiredFlag(): boolean
{
	const vSet = sessionStorage.getItem(kSessionExpiredFlag) === "1";

	if (vSet)
	{
		sessionStorage.removeItem(kSessionExpiredFlag);
	}

	return vSet;
}

export interface AuthState
{
	userId:	string | null;					// Authenticated user id, or null when signed out
	role:	"USER" | "ADMIN" | null;		// Authenticated user role
}

interface AuthContextValue extends AuthState
{
	requestCode:	(inEmail: string) => Promise<void>;					// Triggers /auth/request-code
	verifyCode:		(inEmail: string, inCode: string) => Promise<void>;	// Consumes /auth/verify-code, opens session
	logout:			() => void;											// Clears token and state
	bootstrapping:	boolean;											// True while we restore from storage
}

const fContext = createContext<AuthContextValue | null>(null);	// React context

/*
Decodes the JWT payload (no signature check — the server validates).
Used to recover {userId, role} from the persisted token on page load.
*/
function decodeJwt(
	inToken:	string	// Bearer token
): AuthState | null
{
	try
	{
		const vParts = inToken.split(".");	// header / payload / signature

		if (vParts.length !== 3)
		{
			return null;
		}

		const vJson		= atob((vParts[1] ?? "").replace(/-/g, "+").replace(/_/g, "/"));
		const vPayload	= JSON.parse(vJson) as { sub?: string; role?: string };

		if (typeof vPayload.sub !== "string" || (vPayload.role !== "USER" && vPayload.role !== "ADMIN"))
		{
			return null;
		}

		return { userId: vPayload.sub, role: vPayload.role };
	}
	catch
	{
		return null;
	}
}

/*
Provider component — must wrap the application root.
*/
export function AuthProvider(
	{ children }: { children: ReactNode }
): JSX.Element
{
	const [fState, fSetState]				= useState<AuthState>({ userId: null, role: null });
	const [fBootstrapping, fSetBootstrapping]	= useState<boolean>(true);

	useEffect(() =>
	{
		const vToken = getToken();	// Persisted bearer token

		if (vToken !== null)
		{
			const vDecoded = decodeJwt(vToken);	// Recovered state

			if (vDecoded !== null)
			{
				fSetState(vDecoded);
			}
			else
			{
				setToken(null);
			}
		}

		fSetBootstrapping(false);
	}, []);

	// Register a 401 handler with the api() module. Any authenticated
	// request that comes back unauthorized triggers this — clears the
	// stored token + in-memory state and stamps a sessionStorage flag
	// so /login can render the "session expired" banner. The App
	// auth-gate (userId === null → /login routes) handles navigation.
	useEffect(() =>
	{
		setUnauthorizedHandler(() =>
		{
			setToken(null);
			fSetState({ userId: null, role: null });
			sessionStorage.setItem(kSessionExpiredFlag, "1");
		});

		return () => setUnauthorizedHandler(null);
	}, []);

	const fRequestCode = useCallback(async (inEmail: string) =>
	{
		await api<{ status: string }>(
			"POST",
			"/auth/request-code",
			{ email: inEmail }
		);
	}, []);

	const fVerifyCode = useCallback(async (inEmail: string, inCode: string) =>
	{
		const vRes = await api<{ token: string; userId: string; role: "USER" | "ADMIN" }>(
			"POST",
			"/auth/verify-code",
			{ email: inEmail, code: inCode }
		);

		setToken(vRes.token);
		fSetState({ userId: vRes.userId, role: vRes.role });
	}, []);

	const fLogout = useCallback(() =>
	{
		setToken(null);
		fSetState({ userId: null, role: null });
	}, []);

	const vValue = useMemo<AuthContextValue>(() => (
		{
			userId:			fState.userId,
			role:			fState.role,
			requestCode:	fRequestCode,
			verifyCode:		fVerifyCode,
			logout:			fLogout,
			bootstrapping:	fBootstrapping,
		}
	), [fState, fRequestCode, fVerifyCode, fLogout, fBootstrapping]);

	return <fContext.Provider value={vValue}>{children}</fContext.Provider>;
}

/*
Hook returning the auth context. Throws when used outside of AuthProvider.
*/
export function useAuth(): AuthContextValue
{
	const vCtx = useContext(fContext);	// Current context value

	if (vCtx === null)
	{
		throw new Error("useAuth must be used inside <AuthProvider>");
	}

	return vCtx;
}
